import { LitElement, html } from "lit";
import { glassStyles } from "ui/styles/glass.css.js";
import { toolbarResponsiveStyles } from "ui/styles/toolbar-responsive.css.js";
import { SettingsStore } from "../../main";
import { IntervalDial } from "ui/components/interval-dial.js";
import { IntervalTrigger } from "ui/components/interval-trigger.js";

export class SlideshowToolbar extends LitElement {
  static styles = [glassStyles, toolbarResponsiveStyles];

  static properties = {
    fit: { type: Boolean },
    includeSubdirs: { type: Boolean },
    playing: { type: Boolean },
    layoutName: { type: String },
    intervalSeconds: { type: Number },
    intervalPopupVisible: { type: Boolean },
    showDial: { type: Boolean },
    showMore: { type: Boolean },
    showTransitionMenu: { type: Boolean },
    transitionStyle: { type: String },
    autoPlay: { type: Boolean },
    autoShuffle: { type: Boolean },

    sheetState: { type: String },
    sheetHeight: { type: Number },
  };

  constructor() {
    super();
    this.settings = new SettingsStore();

    this.includeSubdirs = this.settings.includeSubdirs;
    this.fit = this.settings.fit;
    this.intervalSeconds = this.settings.intervalSeconds || 5;

    this.autoPlay = this.settings.autoPlay;
    this.autoShuffle = this.settings.autoShuffle;

    this.playing = false;
    this.layoutName = "";
    this.showDial = false;
    this.showMore = false;
    this.showTransitionMenu = false;
    this.transitionStyle = "soft-glass";

    this.sheetState = "collapsed";
    this.sheetHeight = 60;

    this.dragStartY = 0;
    this.dragCurrentY = 0;
    this.dragStartTime = 0;
    this.isDraggingSheet = false;

    this.collapsedHeight = 60;
    this.halfHeight = window.innerHeight * 0.5;
    this.fullHeight = window.innerHeight * 0.9;

    this._intervalTimeout = null;
  }

  firstUpdated() {
    this._computeSheetHeights();

    // Global click listener
    this._onGlobalClick = (e) => {
      if (!this.intervalPopupVisible) return;

      const path = e.composedPath();
      const root = this.shadowRoot;

      const wrapper = root.querySelector(".interval-wrapper");

      // If the click is NOT inside the wrapper, close the popup
      if (!path.includes(wrapper)) {
        this.intervalPopupVisible = false;
      }
    };

    window.addEventListener("click", this._onGlobalClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("click", this._onGlobalClick);
    this._clearIntervalTimeout();
  }

  updated(changed) {
    if (changed.has("showMore") && this.showMore) {
      this.updateComplete.then(() => {
        this._computeSheetHeights();
        this._setSheetState("half");
      });
    }
  }

  setLayoutName(name) {
    this.layoutName = name;
    this.requestUpdate();
  }

  _computeSheetHeights() {
    const sheet = this.renderRoot.querySelector(".sheet");
    if (!sheet) return;

    const content = sheet.querySelector(".sheet-content");
    if (!content) return;

    const contentHeight = content.scrollHeight;

    this.fullHeight = Math.min(contentHeight + 40, window.innerHeight * 0.9);
    this.halfHeight = window.innerHeight * 0.5;
    this.collapsedHeight = 60;
  }

  _baseHeightForState(state) {
    if (state === "collapsed") return this.collapsedHeight;
    if (state === "half") return this.halfHeight;
    return this.fullHeight;
  }

  _setSheetState(state) {
    this.sheetState = state;

    if (state === "collapsed") this.sheetHeight = this.collapsedHeight;
    if (state === "half") this.sheetHeight = this.halfHeight;
    if (state === "full") this.sheetHeight = this.fullHeight;

    this.requestUpdate();
  }

  // ----------------- DRAG (HANDLE-ONLY, VELOCITY CLOSE) -----------------

  _sheetDragStart(e) {
    this.isDraggingSheet = true;
    this.dragStartY = e.touches[0].clientY;
    this.dragCurrentY = this.dragStartY;
    this.dragStartTime = e.timeStamp;

    const sheet = this.renderRoot.querySelector(".sheet");
    if (sheet) {
      sheet.classList.add("dragging");
      sheet.classList.remove("snap");
    }
  }

  _sheetDragMove(e) {
    if (!this.isDraggingSheet) return;

    this.dragCurrentY = e.touches[0].clientY;
    const delta = this.dragCurrentY - this.dragStartY;

    const baseHeight = this._baseHeightForState(this.sheetState);
    this.sheetHeight = Math.max(60, baseHeight - delta);

    this.requestUpdate();
  }

  _sheetDragEnd(e) {
    if (!this.isDraggingSheet) return;
    this.isDraggingSheet = false;

    const sheet = this.renderRoot.querySelector(".sheet");
    if (sheet) {
      sheet.classList.remove("dragging");
      sheet.classList.add("snap");
    }

    const delta = this.dragCurrentY - this.dragStartY;
    const time = Math.max(1, e.timeStamp - this.dragStartTime);
    const velocity = delta / time;

    const fastSwipe = velocity > 0.6;
    const longSwipe = delta > window.innerHeight * 0.25;

    if (fastSwipe || longSwipe) {
      this.showMore = false;
      return;
    }

    if (delta > 80) {
      if (this.sheetState === "full") this._setSheetState("half");
      else if (this.sheetState === "half") this._setSheetState("collapsed");
      else this.showMore = false;
    } else if (delta < -80) {
      if (this.sheetState === "collapsed") this._setSheetState("half");
      else this._setSheetState("full");
    } else {
      this._setSheetState(this.sheetState);
    }
  }

  // ----------------- TRANSITION SELECTOR -----------------

  _transitionOption(name) {
    return html`
      <div
        class="transition-option ${this.transitionStyle === name
          ? "active"
          : ""}"
        @click=${() => this._selectTransition(name)}
      >
        <span class="swatch ${name}"></span>
        <span class="label">${name}</span>
      </div>
    `;
  }

  _selectTransition(name) {
    this.transitionStyle = name;
    this.showTransitionMenu = false;
    this.showMore = false;
    this._emit("transition-style-change", name);
  }

  // ----------------- RENDER -----------------

  render() {
    return html`
      <div class="toolbar glass-heavy">
        <div class="core-actions">
          <wa-tooltip for="reset-button">Reset Slideshow</wa-tooltip>
          <wa-button
            circle
            size="lg"
            variant="ghost"
            id="reset-button"
            @click=${this._reset}
          >
            <wa-icon name="arrow-rotate-left"></wa-icon>
          </wa-button>

          <wa-tooltip for="prev-button">Previous</wa-tooltip>
          <wa-button
            circle
            size="lg"
            variant="ghost"
            id="prev-button"
            @click=${this._prev}
          >
            <wa-icon name="chevron-left"></wa-icon>
          </wa-button>

          <wa-tooltip for="play-button">Play/Pause</wa-tooltip>
          <wa-button
            circle
            size="lg"
            variant="ghost"
            id="play-button"
            class=${this.playing ? "active" : ""}
            @click=${this._togglePlay}
          >
            <wa-icon name=${this.playing ? "pause" : "play"}></wa-icon>
          </wa-button>

          <wa-tooltip for="next-button">Next</wa-tooltip>
          <wa-button
            circle
            size="lg"
            variant="ghost"
            id="next-button"
            @click=${this._next}
          >
            <wa-icon name="chevron-right"></wa-icon>
          </wa-button>

          <wa-tooltip for="more-button">More</wa-tooltip>
          <wa-button
            class="more-btn"
            circle
            size="lg"
            variant="ghost"
            id="more-button"
            @click=${() => (this.showMore = true)}
          >
            <wa-icon name="ellipsis-vertical"></wa-icon>
          </wa-button>
        </div>

        <div class="full-actions">
          <wa-tooltip for="pick-directory-button">Pick Directory</wa-tooltip>
          <wa-button
            circle
            size="sm"
            variant="ghost"
            id="pick-directory-button"
            @click=${this._pickDirectory}
          >
            <wa-icon name="folder-open"></wa-icon>
          </wa-button>

          <wa-tooltip for="toggle-subdirs-button"
            >Include Subdirectories</wa-tooltip
          >
          <wa-button
            circle
            size="sm"
            variant="ghost"
            id="toggle-subdirs-button"
            @click=${this._toggleSubdirs}
          >
            <wa-icon
              name="folder-tree"
              class=${this.includeSubdirs ? "active" : ""}
            ></wa-icon>
          </wa-button>

          <wa-tooltip for="cycle-layout-button">Cycle Layout</wa-tooltip>
          <wa-button
            circle
            size="sm"
            variant="ghost"
            id="cycle-layout-button"
            @click=${this._cycleLayout}
          >
            <wa-icon name="border-all"></wa-icon>
            <wa-label class="layout-name">${this.layoutName}</wa-label>
          </wa-button>

          <wa-tooltip for="toggle-fit-button">Toggle Fit</wa-tooltip>
          <wa-button
            circle
            size="sm"
            variant="ghost"
            id="toggle-fit-button"
            @click=${this._toggleFit}
          >
            <wa-icon name=${this.fit ? "expand" : "compress"}></wa-icon>
          </wa-button>

          <wa-tooltip for="shuffle-button">Shuffle</wa-tooltip>
          <wa-button
            circle
            size="sm"
            variant="ghost"
            id="shuffle-button"
            @click=${this._shuffle}
          >
            <wa-icon
              name="shuffle"
              class="${this.autoShuffle ? "active" : ""}"
            ></wa-icon>
          </wa-button>

          <wa-tooltip for="auto-play-button">Auto-Play</wa-tooltip>
          <wa-button
            circle
            size="sm"
            variant="ghost"
            id="auto-play-button"
            @click=${this._autoPlay}
          >
            <wa-icon
              name="circle-play"
              class="${this.autoPlay ? "active" : ""}"
            ></wa-icon>
          </wa-button>

          <wa-tooltip for="transition-selector">Transition</wa-tooltip>
          <div class="transition-selector-wrapper" id="transition-selector">
            <wa-button
              class="transition-selector"
              size="sm"
              variant="ghost"
              @click=${() =>
                (this.showTransitionMenu = !this.showTransitionMenu)}
            >
              <wa-icon name="wand-magic-sparkles"></wa-icon>
              <wa-label>Transition: ${this.transitionStyle}</wa-label>
              <wa-icon name="chevron-down"></wa-icon>
            </wa-button>

            ${this.showTransitionMenu
              ? html`
                  <div class="transition-menu">
                    ${this._transitionOption("soft-glass")}
                    ${this._transitionOption("slide")}
                    ${this._transitionOption("radial")}
                    ${this._transitionOption("wipe")}
                    ${this._transitionOption("noise")}
                    ${this._transitionOption("film-burn")}
                  </div>
                `
              : null}
          </div>

          <wa-tooltip for="interval-dropdown">Slideshow Interval</wa-tooltip>
          <div class="interval-wrapper">
            <wa-button size="sm" @click=${this._toggleIntervalPopup}>
              ${this.intervalSeconds}s
            </wa-button>

            <div class="interval-popup" ?visible=${this.intervalPopupVisible}>
              <wa-slider
                id="interval-slider"
                min="1"
                max="30"
                step="1"
                value=${this.intervalSeconds}
                @input=${this._commitInterval}
                @change=${this._commitInterval}
              ></wa-slider>
            </div>
          </div>

          <!-- div class="keyboard-hints">
            <span><strong>Space</strong> Play/Pause</span>
            <span><strong>←</strong> Prev</span>
            <span><strong>→</strong> Next</span>
            <span><strong>↑</strong>/<strong>↓</strong> Layout</span>
            <span><strong>F</strong> Fullscreen</span>
            <span><strong>S</strong> Shuffle</span>
            <span><strong>Esc</strong> Close Zoom</span>
          </div -->
        </div>

        ${this.showMore
          ? html`
              <div
                class="sheet-backdrop"
                @click=${() => (this.showMore = false)}
              ></div>

              <div class="sheet" style="--sheet-height:${this.sheetHeight}px">
                <div
                  class="sheet-dragzone"
                  @touchstart=${this._sheetDragStart}
                  @touchmove=${this._sheetDragMove}
                  @touchend=${this._sheetDragEnd}
                >
                  <div class="sheet-handle"></div>
                </div>

                <div class="sheet-content">
                  <h3>More Tools</h3>

                  <wa-button block variant="ghost" @click=${this._reset}>
                    <wa-icon name="arrow-rotate-left"></wa-icon>
                    Reset Slideshow
                  </wa-button>

                  <wa-button
                    block
                    variant="ghost"
                    @click=${this._pickDirectory}
                  >
                    <wa-icon name="folder-open"></wa-icon>
                    Load Folder
                  </wa-button>

                  <wa-button
                    block
                    variant="ghost"
                    class=${this.includeSubdirs ? "active" : ""}
                    @click=${this._toggleSubdirs}
                  >
                    <wa-icon name="folder-tree"></wa-icon>
                    Include Subfolders
                  </wa-button>

                  <wa-button block variant="ghost" @click=${this._cycleLayout}>
                    <wa-icon name="border-all"></wa-icon>
                    Layout: ${this.layoutName}
                  </wa-button>

                  <wa-button block variant="ghost" @click=${this._toggleFit}>
                    <wa-icon name=${this.fit ? "expand" : "compress"}></wa-icon>
                    Toggle Fit
                  </wa-button>

                  <wa-button block variant="ghost" @click=${this._shuffle}>
                    <wa-icon
                      name="shuffle"
                      class="${this.autoShuffle ? "active" : ""}"
                    ></wa-icon>
                    Shuffle
                  </wa-button>

                  <wa-button
                    circle
                    size="sm"
                    variant="ghost"
                    @click=${this._autoPlay}
                  >
                    <wa-icon
                      name="circle-play"
                      class="${this.autoPlay ? "active" : ""}"
                    ></wa-icon>
                    Auto-Play
                  </wa-button>

                  <h3>Transition Style</h3>
                  ${[
                    "soft-glass",
                    "slide",
                    "radial",
                    "wipe",
                    "noise",
                    "film-burn",
                  ].map(
                    (name) => html`
                      <wa-button
                        block
                        variant="ghost"
                        class=${this.transitionStyle === name ? "active" : ""}
                        @click=${() => this._selectTransition(name)}
                      >
                        <wa-icon name="sparkles"></wa-icon>
                        ${name}
                      </wa-button>
                    `,
                  )}

                  <h3>Interval</h3>

                  <!-- MOBILE SLIDER -->
                  <div class="interval-slider-wrapper">
                    <input
                      type="range"
                      min="1"
                      max="30"
                      step="1"
                      .value=${this.intervalSeconds}
                      @input=${(e) =>
                        this._changeTransitionTime(Number(e.target.value))}
                    />
                    <div class="interval-value">${this.intervalSeconds}s</div>
                  </div>
                </div>
              </div>
            `
          : null}
      </div>
    `;
  }
  _toggleIntervalPopup() {
    this.intervalPopupVisible = !this.intervalPopupVisible;

    if (this.intervalPopupVisible) {
      const slider = this.shadowRoot.getElementById("interval-slider");

      // force an update to ensure the slider is in sync with the current intervalSeconds
      // as it does not initally update correctlyas the popup is closed on start.
      slider.value = this.intervalSeconds;
      this._startIntervalTimeout();
    } else {
      this._clearIntervalTimeout();
    }
  }

  _commitInterval(e) {
    const seconds = Number(e.target.value);
    if (Number.isFinite(seconds)) {
      this._changeTransitionTime(seconds);
    }

    // Reset inactivity timer on release
    this._startIntervalTimeout();
  }

  _startIntervalTimeout() {
    this._clearIntervalTimeout();
    this._intervalTimeout = setTimeout(() => {
      this._closePopup();
    }, 5000); // 5 seconds
  }

  _clearIntervalTimeout() {
    if (this._intervalTimeout) {
      clearTimeout(this._intervalTimeout);
      this._intervalTimeout = null;
    }
  }

  _closePopup() {
    this.intervalPopupVisible = false;
    this._clearIntervalTimeout();
  }

  // ----------------- EVENTS -----------------

  _emit(name, detail = null) {
    this.dispatchEvent(
      new CustomEvent(name, { detail, bubbles: true, composed: true }),
    );
  }

  _pickDirectory = () => this._emit("pick-directory");
  _shuffle = () => {
    this.autoShuffle = !this.autoShuffle;
    this._emit("shuffle", this.autoShuffle);
  };
  _toggleSubdirs = () => {
    this.includeSubdirs = !this.includeSubdirs;
    this._emit("toggle-subdirs", this.includeSubdirs);
  };
  _cycleLayout = () => this._emit("cycle-layout");
  _togglePlay = () => {
    this.playing = !this.playing;
    this._emit("toggle-play", this.playing);
  };
  _toggleFit = () => {
    this.fit = !this.fit;
    this._emit("toggle-fit", this.fit);
  };
  _changeTransitionTime = (seconds) => {
    this.intervalSeconds = seconds;
    this._emit("change-transition-time", seconds);
  };
  _next = () => this._emit("next");
  _prev = () => this._emit("prev");
  _reset = () => {
    this.dispatchEvent(
      new CustomEvent("reset-slideshow", {
        bubbles: true,
        composed: true,
      }),
    );
  };
  _autoPlay = () => {
    this.autoPlay = !this.autoPlay;
    this._emit("toggle-auto-play", this.autoPlay);
  };
}

customElements.define("slideshow-toolbar", SlideshowToolbar);
